// Página full-screen para editar un video del HISTORIAL de redes-sociales con
// IA (Gemini Omni Flash). Feature NUEVA e independiente del editor de Sora
// (`EditarVideo`/`editar_vid`): NO lo reemplaza. Espeja el comportamiento de
// "Editar imagen" del historial (navegación a página completa) pero para videos
// ≤10s, reutilizando el componente `EditarVideoIA`. Ver ADR-500 / ADR-502.
//
// Divergencia con la timeline: el resultado de Gemini vuelve en el bucket
// `video-project-assets`; acá lo copiamos a `generadas` + creamos una fila
// NUEVA en `imagenes_generadas`. En el historial solo hay "Guardar" (item nuevo)
// o "Cancelar": NUNCA se reemplaza ni borra el video original (por eso pasamos
// `singleSave` a EditarVideoIA).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { leerVolverA } from '../utils/volverA';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSidebar } from '../context/SidebarContext';
import { useBrandProfile } from '../hooks/useBrandProfile';
import { MARCO_PANTALLA, BORDE_PANTALLA } from '../components/PantallaChrome';
import {
  EditarVideoIA,
  type EditarVideoIASaveResult,
} from '../components/EditarVideoIA';
import { OMNI_MAX_DURATION_SEC, type OmniAspectRatio } from '../lib/omniEdit';

const GENERADAS_BUCKET = 'generadas';
const HISTORY_ROUTE = '/redes-sociales/imagenes';

function formatoToAspect(formato?: string | null): OmniAspectRatio {
  switch ((formato || '').toLowerCase()) {
    case 'square':
      return '1:1';
    case 'landscape':
      return '16:9';
    case 'portrait':
      return '9:16';
    default:
      return '9:16';
  }
}

/**
 * Extrae el primer frame de un video (por su object-URL local) como PNG, para
 * usarlo de poster/miniatura en el historial. Best-effort: devuelve null si
 * falla o tarda demasiado (el guardado no depende de esto). Usa un blob URL
 * local → el canvas no queda "tainted" (sin problemas de CORS).
 */
function extractPosterPng(src: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    let settled = false;
    const finish = (b: Blob | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      resolve(b);
    };
    const timer = window.setTimeout(() => finish(null), 8000);
    const drawFrame = () => {
      try {
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (!vw || !vh) return finish(null);
        // Miniatura: `opt/` es para thumbnails chicos → reducimos a máx 480px de
        // ancho (mantiene aspecto). Baja mucho el peso del PNG → subida casi
        // instantánea.
        const maxW = 480;
        const scale = vw > maxW ? maxW / vw : 1;
        const w = Math.round(vw * scale);
        const h = Math.round(vh * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return finish(null);
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((b) => finish(b), 'image/png');
      } catch {
        finish(null);
      }
    };
    video.onseeked = drawFrame;
    video.onloadeddata = () => {
      // Buscamos un frame apenas adentro (evita frames negros del arranque).
      try {
        video.currentTime = Math.min(0.1, (video.duration || 1) / 2);
      } catch {
        drawFrame();
      }
    };
    video.onerror = () => finish(null);
    video.src = src;
  });
}

export const EditarVideoIAHistorial: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const imageId = (params.get('id') || '').trim();
  const companyId = (params.get('companyId') || '').trim();
  // Marca activa para estampar en la fila del video editado
  // (ADR-2026-09-13-brand-id-en-imagenes-generadas); el trigger prefiere la del
  // producto si `variante_id` tiene una.
  const { currentBrandId } = useBrandProfile();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clipUrl, setClipUrl] = useState('');
  const [clipPath, setClipPath] = useState('');
  const [durationSec, setDurationSec] = useState<number | null>(null);
  const [aspectRatio, setAspectRatio] = useState<OmniAspectRatio>('9:16');
  // Metadatos de la fila original que copiamos a la fila nueva al guardar.
  const originalMeta = useRef<{
    varianteId: string | null;
    formato: string | null;
  }>({ varianteId: null, formato: null });

  /**
   * Volver al historial — ADR-2026-08-05-volver-del-editor-reabre-el-visor.
   *
   * Si se entró desde el VISOR (el historial lo marca con `volverAlVisor`), se vuelve
   * con `expandImageId`, que es el camino que el historial ya tiene para abrirse
   * mostrando una imagen. Sin eso caía en la grilla y había que buscar el video otra vez
   * entre todos los demás.
   */
  const goBack = () => {
    const st = (location.state as any) || {};
    // Quien abrió el editor dijo a dónde volver — ADR-2026-09-16-volver-a-donde-vine.
    const volverA = leerVolverA(st);
    if (volverA) {
      navigate(volverA.path, { state: volverA.state });
      return;
    }
    navigate(
      HISTORY_ROUTE,
      st.volverAlVisor && st.imagenId
        ? { state: { expandImageId: String(st.imagenId) } }
        : undefined,
    );
  };

  // Botón "atrás" nativo de mobile → volver al historial.
  useEffect(() => {
    setMobileBackOverride(() => () => goBack());
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar la fila + firmar la URL del video original.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!imageId || !companyId) {
        setError('Faltan datos del video para editarlo.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        // Resolver la fila: por id (UUID), fallback file_name exacto o prefijo.
        const sel = 'id, file_name, formato, variante_id, tipo';
        let row:
          | {
              id: string;
              file_name: string;
              formato: string | null;
              variante_id: string | null;
              tipo: string | null;
            }
          | null = null;
        const { data: byId } = await supabase
          .from('imagenes_generadas')
          .select(sel)
          .eq('id', imageId)
          .eq('id_company', companyId)
          .maybeSingle();
        if (byId) row = byId as any;
        if (!row) {
          const { data: byName } = await supabase
            .from('imagenes_generadas')
            .select(sel)
            .eq('file_name', imageId)
            .eq('id_company', companyId)
            .maybeSingle();
          if (byName) row = byName as any;
        }
        if (!row) {
          const { data: byLike } = await supabase
            .from('imagenes_generadas')
            .select(sel)
            .like('file_name', `${imageId}.%`)
            .eq('id_company', companyId)
            .maybeSingle();
          if (byLike) row = byLike as any;
        }
        if (!row?.file_name) throw new Error('El video no está disponible.');

        const path = `${companyId}/full/${row.file_name}`;
        const { data: signed } = await supabase.storage
          .from(GENERADAS_BUCKET)
          .createSignedUrl(path, 3600);
        if (!signed?.signedUrl)
          throw new Error('No se pudo obtener el video para editar.');

        originalMeta.current = {
          varianteId: row.variante_id ?? null,
          formato: row.formato ?? null,
        };
        if (!cancelled) {
          setClipPath(path);
          setClipUrl(signed.signedUrl);
          setAspectRatio(formatoToAspect(row.formato));
        }
      } catch (e: any) {
        if (!cancelled)
          setError(e?.message || 'Error al cargar el video para editar.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [imageId, companyId]);

  // Guardado: baja el mp4 editado (video-project-assets), lo sube a `generadas`
  // y crea una fila NUEVA en `imagenes_generadas`. Nunca reemplaza ni borra el
  // video original. Lanza en caso de error → EditarVideoIA lo muestra y NO cierra.
  const handleSave = async (result: EditarVideoIASaveResult) => {
    // 1) bajar el mp4 editado por su signed URL.
    const resp = await fetch(result.url);
    if (!resp.ok) throw new Error('No se pudo descargar el video editado.');
    const blob = await resp.blob();

    // 2) subir a `generadas` con un nombre único.
    const newFileName = `${crypto.randomUUID()}.mp4`;
    const newPath = `${companyId}/full/${newFileName}`;
    const { error: upErr } = await supabase.storage
      .from(GENERADAS_BUCKET)
      .upload(newPath, blob, { contentType: 'video/mp4', upsert: false });
    if (upErr) throw new Error('No se pudo guardar el video en el historial.');

    // 2b) generar un poster (primer frame) y subirlo a `opt/{nombre}.png` para
    // que el historial muestre miniatura en vez del placeholder gris. El grid
    // busca la miniatura del video justo en esa ruta. Best-effort: si falla, el
    // video igual queda guardado (con placeholder).
    try {
      const blobUrl = URL.createObjectURL(blob);
      const poster = await extractPosterPng(blobUrl);
      URL.revokeObjectURL(blobUrl);
      if (poster) {
        const posterName = newFileName.replace(/\.mp4$/i, '.png');
        await supabase.storage
          .from(GENERADAS_BUCKET)
          .upload(`${companyId}/opt/${posterName}`, poster, {
            contentType: 'image/png',
            upsert: false,
          });
      }
    } catch {
      // El poster es un extra: si falla, no bloquea el guardado.
    }

    // 3) insertar la fila (id/created_at los pone la DB por default).
    const { formato, varianteId } = originalMeta.current;
    const {
      data: { user: yo },
    } = await supabase.auth.getUser();
    const { error: insErr } = await supabase.from('imagenes_generadas').insert({
      id_company: companyId,
      // Quién editó (spec 2026-09-11-invitaciones-equipo §6.2).
      user_id: yo?.id ?? null,
      file_name: newFileName,
      tipo: 'video',
      formato,
      variante_id: varianteId,
      brand_id: currentBrandId ?? null,
      active: true,
    });
    if (insErr) throw new Error('No se pudo registrar el video editado.');
    // El original queda intacto (no se desactiva ni borra): el editado se suma
    // como item NUEVO. Al resolver, EditarVideoIA llama onClose → historial.
  };

  const tooLong = durationSec !== null && durationSec > OMNI_MAX_DURATION_SEC;
  const ready = !!clipUrl && durationSec !== null && !tooLong;

  const cuerpo = (
    <>
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-[15px] text-neutral-700">{error}</p>
            <button
              type="button"
              onClick={goBack}
              className="text-[#1644EB] text-[15px] font-medium"
            >
              Volver al historial
            </button>
          </div>
        ) : tooLong ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-[15px] text-neutral-700">
              Este video es muy largo para editarlo con IA (máx. 10 segundos).
            </p>
            <button
              type="button"
              onClick={goBack}
              className="text-[#1644EB] text-[15px] font-medium"
            >
              Volver al historial
            </button>
          </div>
        ) : ready ? (
          <div className="h-full max-w-content mx-auto w-full">
            <EditarVideoIA
              modoUtilizado="editar_video_omni"
              clipUrl={clipUrl}
              originalPath={clipPath}
              // La URL de arriba se firmó a 1h al abrir la pantalla y esta
              // página se deja abierta (hubo un caso con 21h). Refirmamos al
              // mandar. Ver ADR-2026-09-23-omni-edit-url-vencida.
              refirmarOriginal={async () => {
                const { data } = await supabase.storage
                  .from(GENERADAS_BUCKET)
                  .createSignedUrl(clipPath, 60 * 60 * 3);
                return data?.signedUrl ?? null;
              }}
              durationSec={durationSec as number}
              aspectRatio={aspectRatio}
              // `project_id` en omni_edit_jobs es UUID → usamos el id (UUID) del
              // video del historial (no hay "proyecto" acá). Solo se usa como
              // segmento del storage path scratch de video-project-assets.
              projectId={imageId}
              companyId={companyId}
              onSave={handleSave}
              onClose={goBack}
              showOriginalPreview
              singleSave
              // Layout horizontal (video izq + composer der), como "Editar imagen".
              layout="page"
            />
          </div>
        ) : (
          // Firmado ok pero todavía leyendo la duración del <video>.
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
          </div>
        )}
    </>
  );

  return (
    <div
      className="w-full bg-surface-content min-h-[100dvh]"
      style={{ ['--sb' as any]: `${sidebarWidth}px` }}
    >
      {/* Desktop: barra + cuerpo dentro del MARCO del shell —
          ADR-2026-08-05-los-editores-usan-el-marco-del-shell. Las dos capas van en el
          mismo contenedor `fixed`: separadas no había forma de redondearlas juntas ni de
          que el contenido respetara las esquinas (`overflow-hidden`). */}
      <div
        className={`hidden md:flex md:flex-col fixed right-0 bottom-0 z-40 overflow-hidden bg-surface-content ${MARCO_PANTALLA} ${BORDE_PANTALLA}`}
        style={{
          left: 'var(--sb)',
          // Debajo de la barra de créditos, como los paneles de modo
          // (ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra).
          top: 'calc(var(--shell-barra-creditos, 0px) + 8px)',
        }}
      >
        <div className="barra-editor flex items-center gap-3 h-16 px-6 shrink-0 bg-surface-card border-b border-neutral-200">
          <button
            type="button"
            onClick={goBack}
            className="w-9 h-9 rounded-full flex items-center justify-center text-neutral-600 hover:bg-neutral-100 active:scale-95 transition"
            aria-label="Volver"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-[20px] font-semibold text-brand-black/90 truncate min-w-0">
            Editar video con IA
          </h1>
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={goBack}
              className="h-9 px-2.5 rounded-md text-neutral-700 hover:bg-neutral-100 text-[13px] font-medium inline-flex items-center gap-1.5 transition"
            >
              Cancelar
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">{cuerpo}</div>
      </div>

      {/* Mobile: sin barra propia —el botón flotante del Sidebar hace de volver— y a
          pantalla completa, con `pt-14` para despejarlo. */}
      <div className="md:hidden fixed inset-0 z-30 bg-surface-content overflow-hidden pt-14">
        {cuerpo}
      </div>
      {/* Video oculto para leer la duración antes de montar el editor (el
          historial no la conoce). */}
      {!!clipUrl && durationSec === null && (
        <video
          src={clipUrl}
          preload="metadata"
          muted
          playsInline
          className="hidden"
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            setDurationSec(Number.isFinite(d) && d > 0 ? d : 0);
          }}
          onError={() => setDurationSec(0)}
        />
      )}
    </div>
  );
};

export default EditarVideoIAHistorial;
